/**
 * HTTP binding of SyncTransport for the Centerline Cloud API (browser or Node
 * 18+ fetch), plus a small fetch-based Server-Sent Events reader for clients
 * without EventSource (Node scripts/tests).
 */
import type { CenterlineDocument } from "./types.js";
import { type LogEntry, type SubmitResponse, type SyncTransport, SyncHttpError, SyncOfflineError } from "./sync.js";

export interface HttpTransportOptions {
  baseUrl: string;            // "" for same-origin
  documentId: string;
  headers?: Record<string, string>;
  fetch?: typeof fetch;
}

async function call<T>(o: HttpTransportOptions, path: string, init: RequestInit = {}): Promise<T> {
  const f = o.fetch ?? fetch;
  let res: Response;
  try {
    res = await f(`${o.baseUrl}/api/documents/${o.documentId}${path}`, {
      ...init, credentials: "same-origin",
      headers: { ...(init.body ? { "Content-Type": "application/json" } : {}), ...(o.headers ?? {}), ...((init.headers as Record<string, string>) ?? {}) },
    });
  } catch (err) {
    throw new SyncOfflineError((err as Error).message);
  }
  if (!res.ok) {
    let body: { error?: string; message?: string } = {};
    try { body = (await res.json()) as typeof body; } catch { /* not JSON */ }
    throw new SyncHttpError(res.status, body.error ?? `http_${res.status}`, body.message ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

export function httpTransport(o: HttpTransportOptions): SyncTransport {
  return {
    submit: (req) => call<SubmitResponse>(o, "/ops", { method: "POST", body: JSON.stringify(req) }),
    opsSince: (since) => call(o, `/ops?since=${since}`),
    snapshot: async () => {
      const r = await call<{ meta: { headSeq?: number }; document: CenterlineDocument }>(o, "");
      return { document: r.document, headSeq: r.meta.headSeq ?? 0 };
    },
  };
}

export interface SseHandlers {
  hello?(data: { headSeq: number; since: number }): void;
  op?(entry: LogEntry): void;
  reset?(data: { reason: string }): void;
  error?(err: unknown): void;
}

/** Reads /events with fetch; reconnects with Last-Event-ID until `close()`. */
export function openEventStream(o: HttpTransportOptions & { since: number }, h: SseHandlers): { close(): void } {
  const f = o.fetch ?? fetch;
  let closed = false;
  let last = o.since;
  let controller: AbortController | null = null;
  const run = async () => {
    while (!closed) {
      controller = new AbortController();
      try {
        const res = await f(`${o.baseUrl}/api/documents/${o.documentId}/events?since=${last}`, {
          headers: { Accept: "text/event-stream", "Last-Event-ID": String(last), ...(o.headers ?? {}) }, signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new SyncHttpError(res.status, `http_${res.status}`, "event stream failed");
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          let i: number;
          while ((i = buf.indexOf("\n\n")) >= 0) {
            const block = buf.slice(0, i); buf = buf.slice(i + 2);
            let event = "message", data = "", id: string | null = null;
            for (const line of block.split("\n")) {
              if (line.startsWith("event: ")) event = line.slice(7);
              else if (line.startsWith("data: ")) data += line.slice(6);
              else if (line.startsWith("id: ")) id = line.slice(4);
            }
            if (!data) continue;
            const parsed = JSON.parse(data);
            if (id !== null) last = Number(id);
            if (event === "op") h.op?.(parsed);
            else if (event === "hello") h.hello?.(parsed);
            else if (event === "reset") h.reset?.(parsed);
          }
        }
      } catch (err) {
        if (closed) return;
        h.error?.(err);
      }
      if (!closed) await new Promise((r) => setTimeout(r, 500));
    }
  };
  void run();
  return { close() { closed = true; controller?.abort(); } };
}
